/**
 * ILARIA · Real fit on different bodies (v4)
 * --------------------------------------------------------------------------
 * Behavior:
 *   - Click size chip → switch visible model card AND silently pre-select
 *     that size in the hero's variant picker.
 *   - Click "Select size X" → force-select that size in the hero (even if a
 *     different size was already chosen), then smooth-scroll to it.
 *
 * The size-selector logic tries multiple patterns to cover Be Yours and most
 * other OS 2.0 themes:
 *   - radio inputs by value
 *   - select dropdowns by option value
 *   - <button data-option-value> (variant-radios custom element)
 *   - <label> children
 *   - case-insensitive match throughout
 *   - clicks both the input AND its associated label to defeat any
 *     hidden-input + clickable-label theme patterns
 *
 * Debug:
 *   - Set ILARIA_FIT_DEBUG to true (default) to see console diagnostics.
 *     Useful when a size doesn't pre-select — the log shows what we found
 *     in the hero and which selector eventually matched (or none).
 */
(function () {
  'use strict';

  const ILARIA_FIT_DEBUG = true;

  function log() {
    if (!ILARIA_FIT_DEBUG) return;
    const args = ['[ILARIA Fit]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  function initAll() {
    document.querySelectorAll('[data-ilaria-fit]').forEach(initSection);
  }

  function initSection(section) {
    const chips = section.querySelectorAll('.ilaria-fit__chip');
    const cards = section.querySelectorAll('.ilaria-fit__card');

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        const targetSize = chip.dataset.size;
        chips.forEach(function (c) {
          const isMatch = c.dataset.size === targetSize;
          c.classList.toggle('is-active', isMatch);
          c.setAttribute('aria-selected', isMatch ? 'true' : 'false');
        });
        cards.forEach(function (card) {
          const isMatch = card.dataset.size === targetSize;
          card.classList.toggle('is-active', isMatch);
          card.hidden = !isMatch;
        });
        /* Silent pre-select — no scroll, no warning if it fails. */
        selectSizeInHero(targetSize);
      });
    });

    section.querySelectorAll('[data-ilaria-fit-select]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        handleSelectSize(btn);
      });
    });
  }

  function handleSelectSize(btn) {
    const card = btn.closest('.ilaria-fit__card');
    const targetSize = btn.dataset.size;

    const selected = selectSizeInHero(targetSize);
    scrollToSizeArea();

    if (!selected) {
      showFeedback(
        card,
        "Couldn't pre-select size " + targetSize + ". Please choose it manually above.",
        'warning'
      );
    }
  }

  /**
   * Force-select a size value in the hero's variant picker.
   * Returns true if it was able to target an element that represents that size.
   *
   * Approach order:
   *   1. Radio inputs (with case-insensitive value match)
   *   2. Select dropdowns
   *   3. Buttons with data-option-value (modern variant-radios pattern)
   *   4. Generic [data-value] / [data-option-value] elements
   *   5. Labels with exact text matching the size
   */
  function selectSizeInHero(targetSize) {
    const tNorm = normalize(targetSize);
    log('selectSizeInHero — targetSize:', targetSize, '(normalized:', tNorm + ')');

    /* v6: broader form lookup — Be Yours sometimes wraps form in product-form custom element. */
    const form = document.querySelector('form[action*="/cart/add"]')
      || document.querySelector('product-form form')
      || document.querySelector('form.product-form')
      || document.querySelector('main form');
    if (!form) {
      log('No cart form found — aborting');
      return false;
    }

    /* ----------------- 0. Size radios by NAME (most reliable) ----------------- */
    const sizeRadios = form.querySelectorAll(
      'input[type="radio"][name*="ize"], input[type="radio"][name*="IZE"], input[type="radio"][name="Size"]'
    );
    log('Found size-named radios:', sizeRadios.length);
    for (let i = 0; i < sizeRadios.length; i++) {
      const r = sizeRadios[i];
      if (normalize(r.value) === tNorm) {
        log('Matched size radio by name:', r);
        forceSelectInput(r);
        return true;
      }
    }

    /* ----------------- 1. Radio inputs (fallback, any name) ----------------- */
    const radios = form.querySelectorAll('input[type="radio"]');
    log('Found radios in form:', radios.length, Array.from(radios).map(function (r) {
      return { name: r.name, value: r.value, id: r.id, checked: r.checked };
    }));

    for (let i = 0; i < radios.length; i++) {
      const r = radios[i];
      if (isColorOption(r.name)) continue;
      if (normalize(r.value) === tNorm) {
        log('Matched radio:', r);
        forceSelectInput(r);
        return true;
      }
    }

    /* ----------------- 2. Select dropdowns ----------------- */
    const selects = form.querySelectorAll('select');
    log('Found selects:', selects.length);
    for (let i = 0; i < selects.length; i++) {
      const s = selects[i];
      if (isColorOption(s.name)) continue;
      for (let j = 0; j < s.options.length; j++) {
        if (normalize(s.options[j].value) === tNorm) {
          log('Matched select option:', s.options[j]);
          s.value = s.options[j].value;
          s.dispatchEvent(new Event('input', { bubbles: true }));
          s.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }

    /* ----------------- 3. Buttons with data-option-value ----------------- */
    const dataButtons = form.querySelectorAll('button[data-option-value], button[data-value]');
    log('Found data-option-value buttons:', dataButtons.length);
    for (let i = 0; i < dataButtons.length; i++) {
      const b = dataButtons[i];
      const val = b.dataset.optionValue || b.dataset.value;
      if (val == null) continue;
      const parentName = nearestOptionName(b);
      if (isColorOption(parentName)) continue;
      if (normalize(val) === tNorm) {
        log('Matched data button:', b);
        try { b.click(); } catch (e) { log('Click failed:', e); }
        return true;
      }
    }

    /* ----------------- 4. Generic [data-value] / [data-option-value] ----------------- */
    const dataEls = form.querySelectorAll('[data-option-value], [data-value]');
    log('Found generic data elements:', dataEls.length);
    for (let i = 0; i < dataEls.length; i++) {
      const el = dataEls[i];
      const val = el.dataset.optionValue || el.dataset.value;
      if (val == null) continue;
      const parentName = nearestOptionName(el);
      if (isColorOption(parentName)) continue;
      if (normalize(val) === tNorm) {
        log('Matched generic data element:', el);
        try { el.click(); } catch (e) {}
        return true;
      }
    }

    /* ----------------- 5. Labels with matching exact text ----------------- */
    const labels = form.querySelectorAll('label');
    log('Found labels:', labels.length);
    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      const text = (label.textContent || '').trim();
      if (normalize(text) !== tNorm) continue;
      const parentName = nearestOptionName(label);
      if (isColorOption(parentName)) continue;
      log('Matched label by text:', label);
      try { label.click(); } catch (e) {}
      return true;
    }

    log('No matching element found for size:', targetSize);
    return false;
  }

  /* v6: prefer clicking the LABEL because Shopify themes (incl. Be Yours)
     wire the price/variant-image/SKU update to the native label click via
     <variant-radios> / <variant-selects> custom elements. Manual change events
     alone often update the radio state but not the visible UI. */
  function forceSelectInput(r) {
    /* 1. Try the label by [for=id] — this is the visible button in most themes. */
    if (r.id) {
      const label = document.querySelector('label[for="' + r.id + '"]');
      if (label) {
        try { label.click(); log('Clicked label[for]:', label); return; } catch (e) { log('label[for] click failed:', e); }
      }
    }
    /* 2. Try a parent <label>. */
    const parentLabel = r.closest('label');
    if (parentLabel) {
      try { parentLabel.click(); log('Clicked parent label'); return; } catch (e) {}
    }
    /* 3. Try the radio itself. */
    try { r.click(); log('Clicked radio directly'); return; } catch (e) {}
    /* 4. Last resort: manual state + events. */
    r.checked = true;
    r.dispatchEvent(new Event('input', { bubbles: true }));
    r.dispatchEvent(new Event('change', { bubbles: true }));
    log('Fell back to manual checked + dispatchEvent');
  }

  function normalize(v) {
    return (v == null ? '' : String(v)).trim().toLowerCase();
  }

  function isColorOption(name) {
    const n = normalize(name);
    return n.indexOf('color') >= 0 || n.indexOf('colour') >= 0;
  }

  /* Walk up the DOM looking for the nearest variant-option name. */
  function nearestOptionName(el) {
    let cur = el;
    while (cur && cur !== document) {
      if (cur.dataset && cur.dataset.optionName) return cur.dataset.optionName;
      const legend = cur.querySelector ? cur.querySelector('legend') : null;
      if (legend && cur.tagName && cur.tagName.toLowerCase() === 'fieldset') return legend.textContent || '';
      cur = cur.parentNode;
    }
    return '';
  }

  function scrollToSizeArea() {
    /* v6: scroll up to the product title so the buyer sees title, price,
       description AND the size picker together — not just the size row. */
    const title = document.querySelector('main h1')
      || document.querySelector('[class*="product__title"]')
      || document.querySelector('.product-info h1');
    const form = document.querySelector('form[action*="/cart/add"]');
    const target = title || form;
    if (!target) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    /* Compensate for a sticky header if present, so the title is not hidden behind it. */
    const header = document.querySelector('.shopify-section-header')
      || document.querySelector('header.header')
      || document.querySelector('header');
    const headerHeight = header ? header.offsetHeight : 0;
    const rect = target.getBoundingClientRect();
    const targetY = window.scrollY + rect.top - headerHeight - 16;
    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
  }

  function showFeedback(card, message, type) {
    const feedback = card.querySelector('[data-ilaria-fit-feedback]');
    if (!feedback) return;
    feedback.textContent = message;
    feedback.className = 'ilaria-fit__feedback';
    if (type) feedback.classList.add('is-' + type);
    feedback.hidden = false;
    clearTimeout(feedback._hideTimer);
    feedback._hideTimer = setTimeout(function () {
      feedback.hidden = true;
    }, 5000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
